import type {
  ColorScheme,
  MainButtonParams,
  MiniAppBridge,
  MiniAppUser,
  SafeAreaInsets,
  Unsubscribe,
  ViewportState,
} from './types';
import { createEmitter, createLocalStorage, emptyInsets, saveFileInMessenger } from './shared';

/**
 * Адаптер Telegram Mini Apps (Bot API WebApp).
 *
 * Учитывает, что клиенты Telegram обновляются неравномерно: любой относительно
 * новый метод вызывается через safeCall и не ломает приложение на старых версиях.
 */

type TelegramWebApp = {
  initData: string;
  initDataUnsafe: {
    user?: {
      id: number;
      first_name: string;
      last_name?: string;
      username?: string;
      language_code?: string;
      photo_url?: string;
    };
    start_param?: string;
  };
  version: string;
  platform: string;
  colorScheme: ColorScheme;
  themeParams: Record<string, string>;
  isExpanded: boolean;
  viewportHeight: number;
  viewportStableHeight: number;
  safeAreaInset?: SafeAreaInsets;
  contentSafeAreaInset?: SafeAreaInsets;
  headerColor: string;
  backgroundColor: string;
  BackButton: { show(): void; hide(): void; onClick(cb: () => void): void; offClick(cb: () => void): void };
  MainButton: {
    text: string;
    show(): void;
    hide(): void;
    enable(): void;
    disable(): void;
    showProgress(leaveActive?: boolean): void;
    hideProgress(): void;
    setParams(params: Record<string, unknown>): void;
    onClick(cb: () => void): void;
    offClick(cb: () => void): void;
  };
  HapticFeedback: {
    impactOccurred(style: string): void;
    notificationOccurred(type: string): void;
    selectionChanged(): void;
  };
  CloudStorage?: {
    getItem(key: string, cb: (err: string | null, value?: string) => void): void;
    setItem(key: string, value: string, cb?: (err: string | null) => void): void;
    removeItem(key: string, cb?: (err: string | null) => void): void;
  };
  ready(): void;
  expand(): void;
  close(): void;
  onEvent(event: string, cb: (...args: unknown[]) => void): void;
  offEvent(event: string, cb: (...args: unknown[]) => void): void;
  openLink(url: string, options?: { try_instant_view?: boolean }): void;
  openTelegramLink(url: string): void;
  showAlert(message: string, cb?: () => void): void;
  showConfirm(message: string, cb?: (ok: boolean) => void): void;
  requestWriteAccess?(cb?: (granted: boolean) => void): void;
  downloadFile?(params: { url: string; file_name: string }, cb?: (accepted: boolean) => void): void;
  disableVerticalSwipes?(): void;
  setHeaderColor?(color: string): void;
  setBackgroundColor?(color: string): void;
  enableClosingConfirmation?(): void;
  isVersionAtLeast?(version: string): boolean;
};

declare global {
  interface Window {
    Telegram?: { WebApp?: TelegramWebApp };
  }
}

export function getTelegramWebApp(): TelegramWebApp | null {
  const app = window.Telegram?.WebApp;
  // В обычном браузере SDK отдаёт объект-заглушку без initData: это не Telegram.
  if (!app || typeof app.ready !== 'function') return null;
  return app;
}

function safeCall(action: () => void) {
  try {
    action();
  } catch {
    /* метод недоступен в текущей версии клиента */
  }
}

function toUser(app: TelegramWebApp): MiniAppUser | null {
  const raw = app.initDataUnsafe?.user;
  if (!raw) return null;
  return {
    id: String(raw.id),
    firstName: raw.first_name,
    lastName: raw.last_name,
    username: raw.username,
    languageCode: raw.language_code,
    photoUrl: raw.photo_url,
  };
}

export function createTelegramBridge(app: TelegramWebApp): MiniAppBridge {
  const fallbackStorage = createLocalStorage('bd-tg');
  const themeEmitter = createEmitter<ColorScheme>();
  const viewportEmitter = createEmitter<ViewportState>();

  const readViewport = (): ViewportState => ({
    height: app.viewportHeight || window.innerHeight,
    stableHeight: app.viewportStableHeight || app.viewportHeight || window.innerHeight,
    isExpanded: Boolean(app.isExpanded),
  });

  app.onEvent('themeChanged', () => themeEmitter.emit(app.colorScheme));
  app.onEvent('viewportChanged', () => viewportEmitter.emit(readViewport()));

  const backHandlers = new Set<() => void>();
  const mainHandlers = new Set<() => void>();

  return {
    id: 'telegram',
    name: `Telegram ${app.platform ?? ''}`.trim(),
    isEmbedded: Boolean(app.initData) || app.platform !== 'unknown',

    ready() {
      safeCall(() => app.ready());
      safeCall(() => app.expand());
      // Вертикальные свайпы закрывают мини-апп при скролле длинных экранов
      safeCall(() => app.disableVerticalSwipes?.());
    },
    expand() {
      safeCall(() => app.expand());
    },
    close() {
      safeCall(() => app.close());
    },

    getInitData: () => app.initData ?? '',
    getUser: () => toUser(app),

    getColorScheme: () => app.colorScheme ?? 'light',
    onColorSchemeChange: (handler) => themeEmitter.subscribe(handler),

    getViewport: readViewport,
    onViewportChange: (handler) => viewportEmitter.subscribe(handler),
    getSafeAreaInsets(): SafeAreaInsets {
      const area = app.contentSafeAreaInset ?? app.safeAreaInset;
      if (!area) return emptyInsets;
      return {
        top: area.top ?? 0,
        right: area.right ?? 0,
        bottom: area.bottom ?? 0,
        left: area.left ?? 0,
      };
    },

    setBackButtonVisible(visible) {
      safeCall(() => (visible ? app.BackButton.show() : app.BackButton.hide()));
    },
    onBackButton(handler): Unsubscribe {
      backHandlers.add(handler);
      safeCall(() => app.BackButton.onClick(handler));
      return () => {
        backHandlers.delete(handler);
        safeCall(() => app.BackButton.offClick(handler));
      };
    },

    setMainButton(params: MainButtonParams | null) {
      if (!params) {
        safeCall(() => app.MainButton.hide());
        return;
      }
      safeCall(() => {
        app.MainButton.setParams({
          text: params.text,
          is_visible: params.isVisible ?? true,
          is_active: params.isActive ?? true,
          color: params.color,
          text_color: params.textColor,
        });
        if (params.isProgressVisible) app.MainButton.showProgress(true);
        else app.MainButton.hideProgress();
      });
    },
    onMainButton(handler): Unsubscribe {
      mainHandlers.add(handler);
      safeCall(() => app.MainButton.onClick(handler));
      return () => {
        mainHandlers.delete(handler);
        safeCall(() => app.MainButton.offClick(handler));
      };
    },

    haptic: {
      impact(style = 'light') {
        safeCall(() => app.HapticFeedback.impactOccurred(style));
      },
      notification(type) {
        safeCall(() => app.HapticFeedback.notificationOccurred(type));
      },
      selection() {
        safeCall(() => app.HapticFeedback.selectionChanged());
      },
    },

    storage: {
      get(key) {
        const cloud = app.CloudStorage;
        if (!cloud) return fallbackStorage.get(key);
        return new Promise((resolve) => {
          try {
            cloud.getItem(key, (err, value) => resolve(err ? null : (value ?? null)));
          } catch {
            resolve(null);
          }
        });
      },
      set(key, value) {
        const cloud = app.CloudStorage;
        if (!cloud) return fallbackStorage.set(key, value);
        return new Promise((resolve) => {
          try {
            cloud.setItem(key, value, () => resolve());
          } catch {
            resolve();
          }
        });
      },
      remove(key) {
        const cloud = app.CloudStorage;
        if (!cloud) return fallbackStorage.remove(key);
        return new Promise((resolve) => {
          try {
            cloud.removeItem(key, () => resolve());
          } catch {
            resolve();
          }
        });
      },
    },

    openLink(url, options) {
      safeCall(() => app.openLink(url, { try_instant_view: options?.tryInstantView }));
    },
    openInternalLink(url) {
      safeCall(() => app.openTelegramLink(url));
    },
    share({ url, text }) {
      const target = `https://t.me/share/url?url=${encodeURIComponent(url ?? '')}&text=${encodeURIComponent(text ?? '')}`;
      safeCall(() => app.openTelegramLink(target));
    },

    requestWriteAccess() {
      return new Promise((resolve) => {
        if (typeof app.requestWriteAccess !== 'function') {
          resolve(false);
          return;
        }
        try {
          app.requestWriteAccess((granted) => resolve(Boolean(granted)));
        } catch {
          resolve(false);
        }
      });
    },

    downloadFile({ url, fileName, blob }) {
      return new Promise((resolve) => {
        // Нативный downloadFile работает только с публичным http(s)-адресом
        if (typeof app.downloadFile === 'function' && url.startsWith('http')) {
          try {
            app.downloadFile({ url, file_name: fileName }, (accepted) => resolve(Boolean(accepted)));
            return;
          } catch {
            /* падаем в фолбэк ниже */
          }
        }
        void saveFileInMessenger({ url, fileName, blob }).then(resolve);
      });
    },

    showConfirm(message) {
      return new Promise((resolve) => {
        try {
          app.showConfirm(message, (ok) => resolve(Boolean(ok)));
        } catch {
          resolve(window.confirm(message));
        }
      });
    },
    showAlert(message) {
      return new Promise((resolve) => {
        try {
          app.showAlert(message, () => resolve());
        } catch {
          window.alert(message);
          resolve();
        }
      });
    },
  };
}
